import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('steel_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('steel_token');
      localStorage.removeItem('steel_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // OTP-based login
  sendOTP: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  // Direct login (temporary - bypasses OTP)
  loginDirect: (data) => api.post('/auth/login-direct', data),
  // MSG91 OTP Widget (for mobile)
  getMSG91Config: () => api.get('/auth/msg91/config'),
  checkUserExists: (phone) => api.get(`/auth/msg91/check-user/${phone}`),
  checkUserExistsByEmail: (email) => api.get(`/auth/msg91/check-user-email/${encodeURIComponent(email)}`),
  verifyMSG91Token: (data) => api.post('/auth/msg91/verify', data),
  // Email OTP (backend-based)
  sendEmailOTP: (email) => api.post('/auth/email-otp/send', { email }),
  verifyEmailOTP: (email, otp, userData) => api.post('/auth/email-otp/verify', { email, otp, user_data: userData }),
  resendEmailOTP: (email) => api.post('/auth/email-otp/resend', { email }),
  // Profile
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  // Phone change with OTP
  sendPhoneChangeOTP: (newPhone) => api.post('/auth/change-phone/send-otp', { new_phone: newPhone }),
  verifyPhoneChange: (newPhone, otp) => api.post('/auth/change-phone/verify', { new_phone: newPhone, otp }),
};

// Catalogue API
export const catalogueAPI = {
  getCategories: () => api.get('/catalogue/categories'),
  getSpecifications: (productType) => api.get(`/catalogue/specifications/${productType}`),
  listProducts: (params) => api.get('/catalogue/products', { params }),
};

// Enquiries API
export const enquiriesAPI = {
  create: (data) => api.post('/enquiries', data),
  createFromCalculator: (data) => api.post('/enquiries/calculator', data),
  list: (params) => api.get('/enquiries', { params }),
  getById: (id) => api.get(`/enquiries/${id}`),
  acceptOffer: (enquiryId) => api.post(`/enquiries/${enquiryId}/accept`),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  // Enquiries management
  listEnquiries: (params) => api.get('/admin/enquiries', { params }),
  getEnquiry: (enquiryId) => api.get(`/admin/enquiries/${enquiryId}`),
  createEnquiry: (data) => api.post('/admin/enquiries', data),
  updateEnquiry: (id, data) => api.put(`/admin/enquiries/${id}`, data),
  fullUpdateEnquiry: (id, data) => api.put(`/admin/enquiries/${id}/full`, data),
  deleteEnquiry: (enquiryId) => api.delete(`/admin/enquiries/${enquiryId}`),
  exportEnquiries: (params) => api.get('/admin/enquiries/export', { params, responseType: 'blob' }),
  getEnquiryStats: () => api.get('/admin/enquiries/stats/summary'),
  getTeam: () => api.get('/admin/team'),
  createAdmin: (phone, name) => api.post(`/admin/create-admin?phone=${phone}&name=${name}`),
  // Users management
  listUsers: (params) => api.get('/admin/users', { params }),
  getUserStats: () => api.get('/admin/users/stats'),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  changeUserRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
  // Catalogue management
  listProducts: (params) => api.get('/admin/catalogue/products', { params }),
  getProduct: (productId) => api.get(`/admin/catalogue/products/${productId}`),
  createProduct: (data) => api.post('/admin/catalogue/products', data),
  updateProduct: (productId, data) => api.put(`/admin/catalogue/products/${productId}`, data),
  deleteProduct: (productId) => api.delete(`/admin/catalogue/products/${productId}`),
  getCategories: () => api.get('/admin/catalogue/categories'),
  reseedCatalogue: () => api.post('/admin/catalogue/reseed'),
  // Blog management
  listBlogs: (params) => api.get('/admin/blogs', { params }),
  getBlog: (blogId) => api.get(`/admin/blogs/${blogId}`),
  createBlog: (data) => api.post('/admin/blogs', data),
  updateBlog: (blogId, data) => api.put(`/admin/blogs/${blogId}`, data),
  deleteBlog: (blogId) => api.delete(`/admin/blogs/${blogId}`),
  getBlogCategories: () => api.get('/admin/blogs/categories'),
  reseedBlogs: () => api.post('/admin/blogs/reseed'),
  // Settings management
  getAuthSettings: () => api.get('/admin/settings/auth'),
  updateAuthSettings: (data) => api.put('/admin/settings/auth', data),
  // Calculator settings
  getCalculatorSettings: () => api.get('/admin/settings/calculators'),
  updateCalculatorSettings: (data) => api.put('/admin/settings/calculators', data),
  // WhatsApp settings
  getWhatsAppSettings: () => api.get('/admin/settings/whatsapp'),
  updateWhatsAppSettings: (data) => api.put('/admin/settings/whatsapp', data),
};

// Config API (public)
export const configAPI = {
  getAuthMethods: () => api.get('/config/auth-methods'),
  getCalculatorSettings: () => api.get('/config/calculators'),
  getWhatsAppSettings: () => api.get('/config/whatsapp'),
};

export default api;
